import React, { useState, useEffect } from 'react';
import { AlertCircle, CheckCircle, XCircle, Loader, Brain, Database, Server, Cloud, RefreshCw } from 'lucide-react';

interface ServiceStatus {
  available: boolean;
  binding: string;
}

interface Status {
  ai: ServiceStatus;
  database: ServiceStatus;
  cache: ServiceStatus;
  vectorize: ServiceStatus;
  storage: ServiceStatus;
}

export const AIStatus: React.FC = () => {
  const [status, setStatus] = useState<Status | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<any>(null);
  const [testing, setTesting] = useState(false);

  const checkStatus = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch('https://biig-api.b-9f2.workers.dev/api/ai-debug/status');
      if (response.ok) {
        const data = await response.json();
        setStatus(data);
      } else {
        setError('Failed to fetch status');
      }
    } catch (err) {
      setError('Cannot connect to API');
    } finally {
      setLoading(false);
    }
  };

  const testAI = async () => {
    setTesting(true);
    try {
      const response = await fetch('https://biig-api.b-9f2.workers.dev/api/ai-debug/test-ai', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: 'Hello! Test message.' })
      });
      const data = await response.json();
      setTestResult(data);
    } catch (err) {
      setTestResult({ error: 'Test failed: ' + (err as Error).message });
    } finally {
      setTesting(false);
    }
  };

  const initDatabase = async () => {
    setTesting(true);
    try {
      const response = await fetch('https://biig-api.b-9f2.workers.dev/api/ai-debug/init-db', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      setTestResult(data);
      // Refresh status after DB init
      await checkStatus();
    } catch (err) {
      setTestResult({ error: 'Database init failed: ' + (err as Error).message });
    } finally {
      setTesting(false);
    }
  };

  useEffect(() => {
    checkStatus();
  }, []);

  const StatusIcon = ({ available }: { available: boolean }) => {
    return available ? (
      <CheckCircle className="text-green-500" size={20} />
    ) : (
      <XCircle className="text-red-500" size={20} />
    );
  };

  return (
    <div className="fixed bottom-4 right-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl p-4 w-96 max-h-[600px] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold flex items-center gap-2">
            <Brain className="text-purple-500" size={24} />
            AI System Status
          </h3>
          <button
            onClick={checkStatus}
            disabled={loading}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <RefreshCw className={`${loading ? 'animate-spin' : ''}`} size={16} />
          </button>
        </div>

        {loading && !status && (
          <div className="text-center py-8">
            <Loader className="animate-spin mx-auto text-purple-500" size={32} />
            <p className="mt-2 text-gray-500">Checking status...</p>
          </div>
        )}

        {error && (
          <div className="bg-red-50 text-red-700 p-3 rounded-lg mb-4 flex items-center gap-2">
            <AlertCircle size={20} />
            {error}
          </div>
        )}

        {status && (
          <div className="space-y-3">
            <div className="grid grid-cols-1 gap-2">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Brain size={18} className="text-purple-500" />
                  <span className="font-medium">AI Models</span>
                </div>
                <div className="flex items-center gap-2">
                  <StatusIcon available={status.ai.available} />
                  <span className="text-sm text-gray-600">{status.ai.binding}</span>
                </div>
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Database size={18} className="text-blue-500" />
                  <span className="font-medium">D1 Database</span>
                </div>
                <div className="flex items-center gap-2">
                  <StatusIcon available={status.database.available} />
                  <span className="text-sm text-gray-600">{status.database.binding}</span>
                </div>
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Server size={18} className="text-green-500" />
                  <span className="font-medium">KV Cache</span>
                </div>
                <div className="flex items-center gap-2">
                  <StatusIcon available={status.cache.available} />
                  <span className="text-sm text-gray-600">{status.cache.binding}</span>
                </div>
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Cloud size={18} className="text-cyan-500" />
                  <span className="font-medium">Vectorize</span>
                </div>
                <div className="flex items-center gap-2">
                  <StatusIcon available={status.vectorize.available} />
                  <span className="text-sm text-gray-600">{status.vectorize.binding}</span>
                </div>
              </div>
            </div>

            <div className="border-t pt-3 space-y-2">
              <button
                onClick={testAI}
                disabled={testing || !status.ai.available}
                className="w-full px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {testing ? 'Testing...' : 'Test AI Response'}
              </button>

              {!status.database.available && (
                <button
                  onClick={initDatabase}
                  disabled={testing}
                  className="w-full px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
                >
                  Initialize Database
                </button>
              )}
            </div>

            {testResult && (
              <div className="border-t pt-3">
                <h4 className="font-medium mb-2">Test Result:</h4>
                <pre className="bg-gray-900 text-green-400 p-3 rounded-lg text-xs overflow-x-auto">
                  {JSON.stringify(testResult, null, 2)}
                </pre>
              </div>
            )}

            {!status.ai.available && (
              <div className="bg-yellow-50 text-yellow-800 p-3 rounded-lg">
                <h4 className="font-medium mb-1">⚠️ AI Not Available</h4>
                <p className="text-sm">
                  CloudFlare AI bindings are not configured. To enable:
                </p>
                <ol className="text-xs mt-2 ml-4 list-decimal">
                  <li>Ensure you have AI enabled in your CloudFlare account</li>
                  <li>Check wrangler.toml has [ai] binding = "AI"</li>
                  <li>Redeploy with: npx wrangler deploy</li>
                </ol>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};